/**
* @summary - Contract Edit Form
* @author Yuvasri S
* @since 2024-11-29
*/
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import PageTitle from '../commoncomponents/PageTitle';
import { Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, Grid, Rating, Stack, Typography } from '@mui/material';
import { DefaultCard } from '../../assets/styled-components/card';
import { useLocation } from 'react-router-dom';
import CustomTextField from '../commoncomponents/CustomTextField';
import { getFacilityId } from '../../utility/tokenUtility';
import ContractManagementRepository from '../../api/repositories/contractManagementRepository';
import CustomTextFieldWithSearch from '../commoncomponents/CustomTextFieldWithSearch';
import CustomSelectInput from '../commoncomponents/CustomSelectInput';
import CustomDatePicker from '../commoncomponents/CustomDatePicker';
import moment from 'moment';
import CustomTextArea from '../commoncomponents/CustomTextArea';
import utils from "../../utility/utilities";
import AddIcon from '@mui/icons-material/Add';
import RenewalProcessStatusModal from './RenewalProcessStatusModal';
import CustomAgGrid from '../commoncomponents/CustomAgGrid';
import { format } from 'date-fns';
import { active, inActive, workTypeOptions } from '../../utility/message';
import ContractHistory from './ContractHistory';


const ContractEditForm = forwardRef((props, ref) => {


    function AddRowButton({ onClick }) {
        return (
            <Box sx={{ mt: 2 }}>
                <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={onClick}>
                    Add Row
                </Button>
            </Box>
        );
    };

    const { totalPPMRate } = props; // Add a prop for the callback

    const location = useLocation();
    // console.log("location", location);

    const contractMode = location?.state?.contractmode;

    const viewMode = location?.state?.viewMode;
    const editmode = location?.state?.editmode;
    const getNewContractId = location?.state?.newcontractid;
    const headerId = location?.state?.parentContractId || location?.state?.id;
    const checkIsRenewalForm = location?.state?.isRenewalForm ? true : false;

    const facilityId = getFacilityId('facilityId');
    const initialOldContractState = {
        id: "",
        contractNo: "",
        vendorId: "",
        vendorName: "",
        vendorCode: "",
        contractStartDate: "",
        contractEndDate: "",
        rawContractStartDate: "",
        rawContractEndDate: "",
        totalContractValue: "",
        status: "",
        address: "",
        contractType: "",
        email: "",
        faxNo: "",
        rating: "",
        remarks: "",
        service: "",
        telephoneNo: "",
        totalContractValue: "",
        vendorStatus: "",
        workType: "",
        Purchaseorderno: "",
        Invoiceno: "",
    };

    const initialNewRenewalState = {
        contractNo: '',
        vendorName: '',
        vendorCode: '',
        contractType: '',
        vendorStatus: '',
        workType: [],
        contractStartDate: '',
        contractEndDate: '',
        totalContractValue: totalPPMRate ? Number(totalPPMRate).toFixed(2) : "0",
        rating: 0,
        flag: '',
        pic: '',
        activity: '',
        dateTime: '',
        remarks: '',
        renewalModeStatus: "",
        renewalModeReason: "",
        Purchaseorderno: '',
        Invoiceno: '',
    }
    const [vendorNameOptions, setVendorNameOptions] = useState([]);
    const [contractTypeOptions, setContractTypeOptions] = useState([]);
    const [vendorStatusOptions, setVendorStatusOptions] = useState([]);
    const [rows, setRows] = useState([]);
    const [editData, setEditData] = useState(null);
    const [gridAPI, setGridApi] = useState({});
    const [disableContractNo, setDisableContractNo] = useState(false);
    const statusOptions = [active, inActive];
    const [contractState, setContractState] = useState(initialOldContractState || {});
    const [renewalState, setRenewalState] = useState(initialNewRenewalState || {});
    const [errors, setErrors] = useState({});
    const [disableStatusField, setDisableStatusField] = useState({
        contractMode: false,
        renewalMode: false
    });

    const [reasonField, setReasonField] = useState(false);
    const [reasonFieldForRenewalEditMode, setReasonFieldForRenewalEditMode] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);


    const serviceType = location?.state?.serviceDesc === 'FEMS' ? 'fem' : 'bem';

    const currentDate = utils.getCurrentDateFormatted("DD-MM-YYYY");

    const handleOnChange = (values, name) => {
        setRenewalState((prev) => {
            const updatedState = { ...prev, [name]: values };

            if (name === "vendorName") {
                updatedState.vendorCode = values?.value || "";
            }

            if (name === "renewalModeReason" || name === "renewalModeStatus") {

                if (name === "renewalModeStatus" && !(values?.id)) {
                    setReasonFieldForRenewalEditMode(true);
                } else if (name === "renewalModeStatus" && values?.id) {
                    setRenewalState((prev) => ({
                        ...prev,
                        reason: ""
                    }));
                    setReasonFieldForRenewalEditMode(false);

                }
            };

            return updatedState;
        });
        // Handle validation for required fields
        setErrors((prev) => ({
            ...prev,
            [name]: values ? null : `${name === 'contractNo' ? 'Contract No.' : 'Vendor Name'} is required`,
        }));

    };

    const handleOnchangeForContractState = (values, name) => {
        if (name === "reason" || name === "status") {
            setContractState((prev) => ({
                ...prev,
                [name]: values
            }))

            if (name === "status" && !(values?.id)) {
                setReasonField(true);
                // setReasonErrorState(true);
            } else if (name === "status" && values?.id) {
                setContractState((prev) => ({
                    ...prev,
                    reason: ""
                }));
                setReasonField(false);

            }
        };
        setErrors((prev) => ({
            ...prev,
            reason: null, // Clear any existing error
        }));
    };


    const handleWorkTypeChange = (event) => {
        const { value, checked } = event.target;
        setRenewalState((prev) => ({
            ...prev,
            workType: checked
                ? [...(prev.workType || []), value]
                : (prev.workType || []).filter((service) => service !== value),
        }));

    };

    const handleContractStartDate = (newDate) => {
        if (newDate) {
            let dayin = moment(newDate, 'DD/MM/YYYY', true);
            if (dayin.isValid()) {
                let formattedDate = dayin.format('YYYY-MM-DD HH:mm:ss');
                setRenewalState((prev) => ({
                    ...prev,
                    contractStartDate: formattedDate,
                }));
                setErrors((prev) => ({
                    ...prev,
                    contractStartDate: null, // Clear any existing error
                }));
            } else {
                setErrors((prev) => ({
                    ...prev,
                    contractStartDate: "Contract Start Date is required.",
                }));
            }
        } else {
            setErrors((prev) => ({
                ...prev,
                contractStartDate: "Contract Start Date is required.",
            }));
        }
    };

    const handleContractEndDate = (newDate) => {
        if (newDate) {
            let dayin = moment(newDate, 'DD/MM/YYYY', true);
            if (dayin.isValid()) {
                let formattedDate = dayin.format('YYYY-MM-DD HH:mm:ss');
                setRenewalState((prev) => ({
                    ...prev,
                    contractEndDate: formattedDate,
                }));
                setErrors((prev) => ({
                    ...prev,
                    contractEndDate: null, // Clear any existing error
                }));
            } else {
                setErrors((prev) => ({
                    ...prev,
                    contractEndDate: "Contract End Date is required.",
                }));
            }
        } else {
            setErrors((prev) => ({
                ...prev,
                contractEndDate: "Contract End Date is required.",
            }));
        }
    };

    const handleRatingChange = (e, newValue) => {
        setRenewalState((prev) => ({
            ...prev,
            rating: newValue,
        }));
    };

    const handleDateTimeAuto = (newDate) => {
        if (newDate) {
            let dayin = moment(newDate, 'DD/MM/YYYY', true);
            if (dayin.isValid()) {
                let formattedDate = dayin.format('YYYY-MM-DD HH:mm:ss');
                setRenewalState((prev) => ({
                    ...prev,
                    dateTime: formattedDate
                }));
            }
        }
    };

    const formatDateForDisplay = (date) => {
        return moment(date).format('DD/MM/YYYY');
    };

    const calculateMinimumDate = (date) => {
        return moment(date).add(1, 'days').format('DD/MM/YYYY');
    };

    const getVendorStatus = async () => {
        try {
            const vendorStatus = await ContractManagementRepository.fetchVendorStatus();
            setVendorStatusOptions(vendorStatus);
            return vendorStatus;
        } catch (error) {
            return [];
        }
    };

    const getRenewalProcessAllRows = async (id) => {
        try {
            const getAllRows = await ContractManagementRepository.getRenewalProcessStatus(serviceType, id);
            const updatedRows = getAllRows?.map((item) => ({
                id: item?.id || "",
                flag: { id: item?.contractrenewalflagId || "", label: item?.contractrenewalflagLable || "", value: item?.contractrenewalflagLable || "" },
                pic: { id: item?.contractrenewalpicId || "", label: item?.contractrenewalpicLable || "", value: item?.contractrenewalpicLable || "" },
                activity: { id: item?.contractrenewalactivityId || "", label: item?.contractrenewalactivityLable || "", value: item?.contractrenewalactivityLable || "" },
                dateAndTime: item?.renewaldate || "",
                contractregisterheaderId: item?.contractregisterheaderId || "",
                remarks: item?.renewalremarks || ""
            }));
            setRows(updatedRows || []);
            return updatedRows;
        } catch (error) {
            console.error("Error fetching vendor status:", error);
            return [];
        }
    };

    const getLov = async () => {
        try {
            const vendorName = await ContractManagementRepository.fetchVendorName();
            setVendorNameOptions(vendorName);
            const getLOVs = await ContractManagementRepository.getAllLovValues();
            const ContractTypes = getLOVs?.femcontracttypes?.map((item) => (
                {
                    key: `option${item?.id}`,
                    label: item?.contracttype || "",
                    id: item?.id || "",
                    value: item?.contracttype || ""
                }));
            setContractTypeOptions(ContractTypes || []);

            if (getLOVs?.femcontracttypes?.length > 0) {
                if(location?.state?.isRenewalForm)
                {

                    handleOnChange(ContractTypes?.[0], "contractType");
                }
            }


        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    useEffect(() => {

        getLov();
        getVendorStatus();
        if (headerId) {
            getRenewalProcessAllRows(headerId);
        };
    }, []);

    const validateFields = async () => {
        const requiredFields = ['contractNo', 'vendorName', 'contractStartDate', 'contractEndDate'];
        const newErrors = {};

        // Validate required fields
        requiredFields.forEach((field) => {
            if (
                !renewalState[field] ||
                (field === 'vendorName' && !renewalState[field].label) // Specific check for vendorName
            ) {
                newErrors[field] = `${field === 'contractNo'
                    ? 'Contract No.'
                    : field === 'vendorName'
                        ? 'Vendor Name'
                        : field === 'contractStartDate'
                            ? 'Contract Start Date'
                            : field === 'contractEndDate'
                                ? 'Contract End Date'
                                : ''
                    } is required.`;
            }
        });

        // Additional validation for dates
        const contractStartDate = moment(renewalState.contractStartDate, true);
        const contractEndDate = moment(renewalState.contractEndDate, true);

        if (renewalState.contractStartDate && !contractStartDate.isValid()) {
            newErrors.contractStartDate = 'Contract Start Date is required.';
        }

        if (renewalState.contractEndDate && !contractEndDate.isValid()) {
            newErrors.contractEndDate = 'Contract End Date is required.';
        }

        if (
            contractStartDate.isValid() &&
            contractEndDate.isValid() &&
            contractEndDate.isSameOrBefore(contractStartDate)
        ) {
            newErrors.contractEndDate = 'Contract End Date must be after Contract Start Date.';
        }

        // Validate reason field if required
        if (reasonField && !(contractState?.reason)) {
            newErrors.reason = 'Reason is required.';
        }

        if (reasonFieldForRenewalEditMode && !(renewalState?.renewalModeReason)) {
            newErrors.renewalModeReason = "Reason is required.";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            // setReasonErrorState(!!newErrors.reason); // Set reason error state if needed
            // setReasonErrorStateForRenewal(!!newErrors.renewalModeReason)
            return;
        }

        setErrors({}); // Clear errors if validation passes
        // setReasonErrorState(false);
        // setReasonErrorStateForRenewal(false);
        return true; // Validation passed
    };

    const handleSubmit = async () => {
        let response;
        const requiredFields = ['contractNo', 'vendorName', 'contractStartDate', 'contractEndDate'];
        const newErrors = {};

        // Validate required fields
        requiredFields.forEach((field) => {
            if (
                !renewalState[field] ||
                (field === 'vendorName' && !renewalState[field].label) // Specific check for vendorName 
            ) {
                newErrors[field] = `${field === 'contractNo'
                    ? 'Contract No.'
                    : field === 'vendorName'
                        ? 'Vendor Name'
                        : field === 'contractStartDate'
                            ? 'Contract Start Date'
                            : field === 'contractEndDate'
                                ? 'Contract End Date'
                                : ''
                    } is required.`;
            }
        });

        // Additional validation for dates
        const contractStartDate = moment(renewalState.contractStartDate, true);
        const contractEndDate = moment(renewalState.contractEndDate, true);
        // const contractStartDate = moment(renewalState.contractStartDate, 'YYYY-MM-DD HH:mm:ss', true);
        // const contractEndDate = moment(renewalState.contractEndDate, 'YYYY-MM-DD HH:mm:ss', true);

        if (renewalState.contractStartDate && !contractStartDate.isValid()) {
            newErrors.contractStartDate = 'Contract Start Date is required.';
        }

        if (renewalState.contractEndDate && !contractEndDate.isValid()) {
            newErrors.contractEndDate = 'Contract End Date is required.';
        }

        if (
            contractStartDate.isValid() &&
            contractEndDate.isValid() &&
            contractEndDate.isSameOrBefore(contractStartDate)
        ) {
            newErrors.contractEndDate = 'Contract End Date must be after Contract Start Date.';
        }
        // Validate contractNo for only spaces
        if (renewalState.contractNo && renewalState.contractNo.trim() === '') {
            newErrors.contractNo = 'Contract No. cannot contain only spaces';
        }
        // Validate reason field if required
        if (reasonField && !(contractState?.reason)) {
            newErrors.reason = 'Reason is required.';
        }

        if (reasonFieldForRenewalEditMode && !(renewalState?.renewalModeReason)) {

            newErrors.renewalModeReason = "Reason is required.";
        }


        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            // setReasonErrorState(!!newErrors.reason); // Set reason error state if needed
            // setReasonErrorStateForRenewal(!!newErrors.renewalModeReason)

            return;
        }



        const serviceType = location?.state?.serviceDesc === 'FEMS' ? 'fem' : 'bem';
        const renewalUpadtePayload = {
            facilityId: facilityId || "",
            contractno: renewalState?.contractNo || "",
            vendorid: renewalState?.vendorName?.id || "",
            serviceid: location?.state?.serviceid || "",
            startdate: renewalState?.contractStartDate || "",
            enddate: renewalState?.contractEndDate || "",
            rating: renewalState?.rating || "",
            worktype: renewalState?.workType && renewalState?.workType?.length > 0 ? renewalState?.workType?.join(',') : '',
            totalcontractvalue: renewalState?.totalContractValue || 0.00,
            [`${serviceType}contracttypeId`]: renewalState?.contractType?.id || "",
            [`${serviceType}vendorstatusId`]: renewalState?.vendorStatus?.id || "",
            [`${serviceType}contractrenewalflagId`]: renewalState?.flag?.id || "",
            [`${serviceType}contractrenewalpicId`]: renewalState?.pic?.id || "",
            [`${serviceType}contractrenewalactivityId`]: renewalState?.activity?.id || "",
            effectivedate: renewalState?.dateTime || "",
            renewalremarks: renewalState?.remarks || "",
            vendorname: renewalState?.vendorName?.label || "",
            [`${serviceType}contractstatusId`]: (checkIsRenewalForm ? active?.fieldID : ((renewalState?.renewalModeStatus?.id) ? active?.fieldID : inActive?.fieldID)) || "",
            isactive: checkIsRenewalForm ? active?.id : (renewalState?.renewalModeStatus?.id || ""),
            inactivereason: renewalState?.renewalModeReason || "",
            isrenewal: editmode ? location?.state?.isrenewal : false,
            id: checkIsRenewalForm ? "" : getNewContractId || "",
            purchaseorderno: renewalState?.Purchaseorderno || "",
            invoiceno: renewalState?.Invoiceno || "",
        };

        const inactiveOldContractpayload = {
            facilityId: facilityId || "",
            contractno: contractState?.contractNo || "",
            vendorid: contractState?.vendorId || "",
            serviceid: contractState?.service?.id || "",
            startdate: contractState?.rawContractStartDate || "",
            enddate: contractState?.rawContractEndDate || "",
            vendorname: contractState?.vendorName?.label || "",
            worktype: contractState?.workType && contractState?.workType?.length > 0 ? contractState?.workType?.join(',') : '',
            totalcontractvalue: contractState?.totalContractValue || 0.00,
            address: contractState?.address || "",
            telephoneno: contractState?.telephoneNo || "",
            faxno: contractState?.faxNo || "",
            email: contractState?.email || "",
            remarks: contractState?.remarks || "",
            rating: contractState?.rating || "",
            [`${serviceType}contractstatusId`]: (checkIsRenewalForm
                ? inActive?.fieldID
                : contractState?.status?.id
                    ? active?.fieldID
                    : inActive?.fieldID) || "",
            isactive: checkIsRenewalForm ? inActive?.id : contractState?.status?.id || "",
            inactivereason: checkIsRenewalForm ? "Renewed" : contractState?.reason || "",
            [`${serviceType}contracttypeId`]: contractState?.contractType?.id || "",
            [`${serviceType}vendorstatusId`]: contractState?.vendorStatus?.id || "",
            isrenewal: true,//Renewed Mode previous record
            id: contractState?.id || "",
            purchaseorderno: contractState?.Purchaseorderno || "",
            invoiceno: contractState?.Invoiceno || "",
        };

        const renewalProcessStatusPayload = rows?.map((row) => ({
            renewaldate: row?.dateAndTime || "",
            renewalremarks: row?.remarks || "",
            [`${serviceType}contractrenewalflagId`]: row?.flag?.id || "",
            [`${serviceType}contractrenewalpicId`]: row?.pic?.id || "",
            [`${serviceType}contractrenewalactivityId`]: row?.activity?.id || "",
            [`${serviceType}contractregisterheaderId`]: headerId || "",
            id: row?.id || "",
        }));

        if (getNewContractId && !checkIsRenewalForm) {
            response = await ContractManagementRepository.updateContractManagement(serviceType, renewalUpadtePayload);
            // await ContractManagementRepository.updateRenewalProcessStatus(serviceType,renewalProcessStatusPayload);            
        } else {
            response = await ContractManagementRepository.createContractManagement(serviceType, renewalUpadtePayload);
        }

        if (response?.id && !renewalState?.renewalModeStatus?.id) {
            setDisableStatusField((prev) => ({
                ...prev,
                renewalMode: true
            }));
        };


        if (response && renewalProcessStatusPayload?.length > 0) {
            await ContractManagementRepository.createRenewalProcessStatus(serviceType, renewalProcessStatusPayload);
            await getRenewalProcessAllRows(headerId);
        };

        const contractModeesponse = await ContractManagementRepository.updateContractManagement(serviceType, inactiveOldContractpayload);
        if (contractModeesponse?.id && inactiveOldContractpayload?.isactive)
            setDisableStatusField((prev) => ({
                ...prev,
                contractMode: false
            }));

        if (!response || !response.id) {
            return response;
        } else {
            setDisableContractNo(true);
        }

        // Step 2: Create Contract Master


        let contractMasterResponse;
        if (response?.id) {
            const contractMasterPayload = {
                [`${serviceType}contractregisterheaderId`]: location?.state?.id || "",
                newcontractid: response?.id || "",
                parentcontractid: location?.state?.parentContractId ? location?.state?.parentContractId : location?.state?.id
            };

            const updateContractMasterPayload = {
                oldid: location?.state?.id || "",
                newid: response?.id || "",
                parentcontractid: location?.state?.parentContractId ? location?.state?.parentContractId : location?.state?.id
            };

            if (checkIsRenewalForm) {
                if (location?.state?.parentContractId) {
                    contractMasterResponse = await ContractManagementRepository.updateContractMaster(serviceType, updateContractMasterPayload);
                } else {
                    contractMasterResponse = await ContractManagementRepository.createContractMaster(serviceType, contractMasterPayload);
                    contractMasterResponse = await ContractManagementRepository.updateContractMaster(serviceType, updateContractMasterPayload);
                }
            }

        }

        return response;
    };

    // Expose handleSubmit to the parent via ref
    useImperativeHandle(ref, () => ({
        handleSubmit,
        serviceType,
        oldId: location?.state?.id,
        parentContractId: location?.state?.parentContractId || location?.state?.id || "",
        validateFields
    }));

    const fetchRenewalData = async () => {
        const contractID = getNewContractId || location?.state?.id;
        if (contractID) {
            try {
                const renewalRes = await ContractManagementRepository.fetchRenewalDataForEdit(serviceType, contractID);
                const vendorStatusOptionsList = await getVendorStatus();
                const getvendorStatusOptionsRenewalMode = vendorStatusOptionsList?.find((option) => option?.id === renewalRes[0]?.[`${serviceType}vendorstatusId`]);
                const getvendorStatusOptionsContractMode = vendorStatusOptionsList?.find((option) => option?.id === renewalRes[1]?.[`${serviceType}vendorstatusId`]);

                if (Array.isArray(renewalRes) && renewalRes.length > 0) {
                    if (!checkIsRenewalForm) {
                        setRenewalState({
                            contractNo: renewalRes[0]?.newContractno || "",
                            vendorName: {
                                id: renewalRes[0]?.vendorid || "",
                                label: renewalRes[0]?.vendorname || "",
                            },
                            vendorCode: renewalRes[0]?.vendorcode || "",
                            contractType: {
                                id: renewalRes[0]?.[`${serviceType}contracttypeId`] || "",
                                label: renewalRes[0]?.contracttype || "",
                                value: renewalRes[0]?.contracttype || "",
                            },
                            workType: renewalRes[0]?.worktype ? renewalRes[0]?.worktype?.split(",") : [],
                            vendorStatus: getvendorStatusOptionsRenewalMode,
                            contractStartDate: renewalRes[0]?.startdate || "",
                            contractEndDate: renewalRes[0]?.enddate || "",
                            totalContractValue: renewalRes[0]?.totalcontractvalue || "",
                            rating: renewalRes[0]?.rating || "",
                            flag: {
                                id: renewalRes[0]?.[`${serviceType}contractrenewalflagId`] || "",
                                label: renewalRes[0]?.flag || ""
                            },
                            pic: {
                                id: renewalRes[0]?.[`${serviceType}contractrenewalpicId`] || "",
                                label: renewalRes[0]?.pic || "",
                            },
                            activity: {
                                id: renewalRes[0]?.[`${serviceType}contractrenewalactivityId`] || "",
                                label: renewalRes[0]?.activity || "",
                            },
                            dateTime: renewalRes[0]?.dateTime || "",
                            remarks: renewalRes[0]?.renewalremarks || "",
                            renewalModeReason: renewalRes[0]?.reason || "",
                            renewalModeStatus: renewalRes[0]?.isactive ? active : inActive,
                            Purchaseorderno: renewalRes[0]?.purchaseorderno || "",
                            Invoiceno: renewalRes[0]?.invoiceno || "",
                            id: renewalRes[0]?.id || ""
                        });
                        if (!renewalRes[0]?.isactive) {
                            setReasonFieldForRenewalEditMode(true);
                            setDisableStatusField((prev) => ({
                                ...prev,
                                renewalMode: true
                            }));
                        };


                    };
                    if (checkIsRenewalForm) {
                        setContractState({
                            id: renewalRes[0]?.id || location?.state?.id || "",
                            contractNo: renewalRes[0]?.oldContractno || location?.state?.contractno || "",
                            vendorId: renewalRes[0]?.vendorid || location?.state?.vendorid || "",
                            vendorName: renewalRes[0]?.vendorname || location?.state?.vendorname || "",
                            vendorCode: renewalRes[0]?.vendorcode || location?.state?.vendorcode || "",
                            rawContractStartDate: renewalRes[0]?.startdate || location?.state?.startdate || "",
                            rawContractEndDate: renewalRes[0]?.enddate || location?.state?.enddate || "",
                            contractStartDate: formatDateForDisplay(renewalRes[0]?.startdate) || location?.state?.startdate || "",
                            contractEndDate: formatDateForDisplay(renewalRes[0]?.enddate) || location?.state?.enddate || "",
                            totalContractValue: renewalRes[0]?.totalcontractvalue || location?.state?.totalcontractvalue || "",
                            status: renewalRes[0]?.isactive ? active : inActive,
                            // status:(renewalRes?.length > 1) ?  (renewalRes[0]?.isactive ? active : inActive) : "",
                            reason: renewalRes[0]?.reason || "",
                            address: renewalRes[0]?.address || "",
                            contractType: { id: renewalRes[0]?.[`${serviceType}contracttypeId`] || "", label: renewalRes[0]?.contracttype || "", value: renewalRes[0]?.contracttype || "" } || "",
                            email: renewalRes[0]?.email || "",
                            faxNo: renewalRes[0]?.faxno || "",
                            rating: renewalRes[0]?.rating || "",
                            remarks: renewalRes[0]?.remarks || "",
                            service: { id: renewalRes[0]?.serviceid || "", label: renewalRes[0]?.serviceDesc || "", value: renewalRes[0]?.serviceDesc || "" } || "",
                            telephoneNo: renewalRes[0]?.telephoneno || "",
                            vendorStatus: getvendorStatusOptionsContractMode || "",
                            workType: renewalRes[0]?.worktype ? renewalRes[0]?.worktype.split(",") : [],
                            Purchaseorderno: renewalRes[0]?.purchaseorderno || "",
                            Invoiceno: renewalRes[0]?.invoiceno || "",
                        });
                        // setReasonField(!(renewalRes[0]?.isactive) ? true : false);
                        if (!(renewalRes[0]?.isactive)) {
                            setDisableStatusField((prev) => ({
                                ...prev,
                                contractMode: true
                            }));
                            setReasonField(true);
                        };
                    } else {
                        setContractState({
                            id: renewalRes[1]?.id || location?.state?.id || "",
                            contractNo: renewalRes[1]?.oldContractno || location?.state?.contractno || "",
                            vendorId: renewalRes[1]?.vendorid || location?.state?.vendorid || "",
                            vendorName: renewalRes[1]?.vendorname || location?.state?.vendorname || "",
                            vendorCode: renewalRes[1]?.vendorcode || location?.state?.vendorcode || "",
                            rawContractStartDate: renewalRes[1]?.startdate || location?.state?.startdate || "",
                            rawContractEndDate: renewalRes[1]?.enddate || location?.state?.enddate || "",
                            contractStartDate: formatDateForDisplay(renewalRes[1]?.startdate) || location?.state?.startdate || "",
                            contractEndDate: formatDateForDisplay(renewalRes[1]?.enddate) || location?.state?.enddate || "",
                            totalContractValue: renewalRes[1]?.totalcontractvalue || location?.state?.totalcontractvalue || "",
                            status: (renewalRes?.length > 1) ? (renewalRes[1]?.isactive ? active : inActive) : (renewalRes[0]?.isactive ? active : inActive),
                            reason: renewalRes[1]?.reason || "",
                            address: renewalRes[1]?.address || "",
                            contractType: { id: renewalRes[1]?.[`${serviceType}contracttypeId`] || "", label: renewalRes[1]?.contracttype || "", value: renewalRes[1]?.contracttype || "" } || "",
                            email: renewalRes[1]?.email || "",
                            faxNo: renewalRes[1]?.faxno || "",
                            rating: renewalRes[1]?.rating || "",
                            remarks: renewalRes[1]?.remarks || "",
                            service: { id: renewalRes[1]?.serviceid || "", label: renewalRes[1]?.serviceDesc || "", value: renewalRes[1]?.serviceDesc || "" } || "",
                            telephoneNo: renewalRes[1]?.telephoneno || "",
                            vendorStatus: getvendorStatusOptionsContractMode || "",
                            workType: renewalRes[1]?.worktype ? renewalRes[1]?.worktype.split(",") : [],
                            Purchaseorderno: renewalRes[1]?.purchaseorderno || "",
                            Invoiceno: renewalRes[1]?.invoiceno || "",
                        });
                        const checkTheStatus = (renewalRes?.length > 1) ? (!(renewalRes[1]?.isactive) ? true : false) : (!(renewalRes[0]?.isactive) ? true : false)
                        if (checkTheStatus) {
                            setDisableStatusField((prev) => ({
                                ...prev,
                                contractMode: true
                            }));
                            setReasonField(true);
                        };
                    }

                    // if((renewalRes?.length > 1) ? !renewalRes[1]?.isactive : false){                        
                    //     setReasonField(true)
                    // };
                } else {
                    console.error("Renewal data is empty or invalid:", renewalRes);
                }
            } catch (error) {
                console.error("Error fetching renewal data:", error);
            }
        }
    };

    useEffect(() => {
        fetchRenewalData();
    }, [serviceType, getNewContractId, checkIsRenewalForm]);


    const formatDate = (dateString) => {
        if (!dateString) {
            return '';
        };
        const formattedDate = format(new Date(dateString), 'dd/MM/yyyy'); // Format the date as 'dd/MM/yyyy HH:mm:ss'
        return formattedDate;
    };

    const columns = [
        {
            field: 'flag',
            headerName: 'Flag',
            flex: 1,
            minWidth: 120,
            valueGetter: (params) => params?.data?.flag ? params?.data.flag?.label : '',
        },
        {
            field: 'pic',
            headerName: 'PIC',
            flex: 1,
            minWidth: 120,
            valueGetter: (params) => params?.data?.pic ? params?.data.pic?.label : '',
        },
        {
            field: 'activity',
            headerName: 'Activity',
            flex: 1,
            minWidth: 120,
            valueGetter: (params) => params?.data?.activity ? params?.data.activity?.label : '',
        },
        {
            field: 'dateAndTime',
            headerName: 'Date/Time',
            flex: 1,
            minWidth: 120,
            valueGetter: (params) => formatDate(params?.data?.dateAndTime),
        },
        {
            field: 'remarks',
            headerName: 'Remarks',
            flex: 1,
            minWidth: 120,
            // valueGetter: (params) => params?.data?.flag ? params?.data.flag?.label : '',
        },
    ];

    useEffect(() => {
        // Update the totalContractValue when totalPPMRate changes
        setRenewalState((prevState) => ({
            ...prevState,
            totalContractValue: totalPPMRate.toFixed(2),
        }));
    }, [totalPPMRate]);

    const handleOpenDialog = () => {
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setEditData(null);
        setDialogOpen(false);
    };
    const handleConfirm = () => {
        handleCloseDialog();
    };


    const handleEdit = (row) => {
        setEditData(row);
        setDialogOpen(true);
    };

    return (
        <>
            <PageTitle
                title={location?.state?.title}
            />
            <DefaultCard sx={{ height: "100%" }}>
                <Typography variant='title2'>Contract Management Existing Data</Typography>
                <Box mb={2} />
                <form onSubmit={handleSubmit} style={{ position: "relative" }}>
                    <Box mb={2}>
                        <Grid container gap={3}>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Contract No."
                                    placeholder={""}
                                    id="contractNo"
                                    name="contractNo"
                                    disabled
                                    value={contractState?.contractNo || ""}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Start Date"
                                    placeholder={""}
                                    id="startDate"
                                    name="startDate"
                                    disabled
                                    value={contractState?.contractStartDate || ""}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="End Date"
                                    placeholder={""}
                                    id="endDate"
                                    name="endDate"
                                    disabled
                                    value={contractState?.contractEndDate || ""}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Purchase Order No."
                                    placeholder={""}
                                    id="purchaseOrderNo"
                                    name="Purchaseorderno"
                                    disabled
                                    value={contractState?.Purchaseorderno || ""}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Invoice No."
                                    placeholder={""}
                                    id="invoiceNo"
                                    name="Invoiceno"
                                    disabled
                                    value={contractState?.Invoiceno || ""}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Total Contract Value"
                                    placeholder={""}
                                    id="totalContractValue"
                                    name="totalContractValue"
                                    disabled
                                    value={contractState?.totalContractValue || ""}
                                />
                            </Grid>

                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Vendor Code"
                                    placeholder={""}
                                    id="vendorCode"
                                    name="vendorCode"
                                    disabled
                                    value={contractState?.vendorCode || ""}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Vendor Name"
                                    placeholder={""}
                                    id="vendorName"
                                    name="vendorName"
                                    disabled
                                    value={contractState?.vendorName || ""}
                                />
                            </Grid>


                            {!checkIsRenewalForm && (
                                <>
                                    <Grid item xs={6} md={3.8}>
                                        <CustomSelectInput
                                            disableClearable={true}
                                            label="Status (Note: To Terminate, select 'Inactive')"
                                            value={contractState?.status || ""}
                                            onChange={handleOnchangeForContractState}
                                            options={statusOptions}
                                            id="status"
                                            name="status"
                                            disabled={disableStatusField?.contractMode || viewMode}
                                        />
                                    </Grid>
                                    {reasonField && (<Grid item xs={6} md={3.8}>
                                        <CustomTextArea
                                            label="Reason"
                                            id="reason"
                                            name="reason"
                                            value={contractState?.reason || ""}
                                            onChange={(e) => handleOnchangeForContractState(e, "reason")}
                                            maxlength={250}
                                            error={!!errors?.reason}
                                            helperText={errors?.reason || ""}
                                            isMandatoryField={reasonField}
                                            disabled={disableStatusField?.contractMode || viewMode}
                                        />
                                    </Grid>)}
                                </>
                            )}
                        </Grid>
                    </Box>
                </form>
            </DefaultCard>
            <Box mb={2} />
            <DefaultCard sx={{ height: "100%" }}>
                <Typography variant='title2'>Contract Management Renewal Data</Typography>
                <Box mb={2} />
                <form onSubmit={handleSubmit} style={{ position: "relative" }}>
                    <Box mb={2}>
                        <Grid container gap={3}>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Service"
                                    placeholder={""}
                                    id="service"
                                    name="service"
                                    disabled
                                    value={location.state.serviceDesc}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Contract No."
                                    isMandatoryField={true}
                                    value={renewalState.contractNo}
                                    onChange={handleOnChange}
                                    id="contractNo"
                                    name="contractNo"
                                    disabled={viewMode || !checkIsRenewalForm ? true : false || disableContractNo}
                                    error={!!errors.contractNo}
                                    errorMessage={errors.contractNo}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomSelectInput
                                    disableClearable={true}
                                    label="Contract Type"
                                    value={renewalState.contractType}
                                    onChange={handleOnChange}
                                    options={contractTypeOptions}
                                    id="contractType"
                                    name="contractType"
                                    disabled={location.state.renewalmode || viewMode}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomDatePicker
                                    label="Contract Start Date"
                                    id="contractStartDate"
                                    name="contractStartDate"
                                    value={formatDateForDisplay(renewalState.contractStartDate)}
                                    onDateChange={handleContractStartDate}
                                    isMandatoryField={true}
                                    error={!!errors.contractStartDate}
                                    errorMessage={errors.contractStartDate}
                                    disabled={location.state.renewalmode || viewMode}
                                // minimumDate={location?.state?.enddate || ""}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomDatePicker
                                    label="Contract End Date"
                                    id="contract-end-date"
                                    name="contractEndDate"
                                    value={formatDateForDisplay(renewalState.contractEndDate)}
                                    onDateChange={handleContractEndDate}
                                    minimumDate={calculateMinimumDate(renewalState.contractStartDate)}
                                    disabled={location.state.renewalmode || viewMode}
                                    maxDate={new Date()}
                                    isMandatoryField={true}
                                    error={!!errors.contractEndDate}
                                    errorMessage={errors.contractEndDate}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Purchase Order No."
                                    value={renewalState.Purchaseorderno}
                                    onChange={handleOnChange}
                                    id="purchaseOrderNo"
                                    name="Purchaseorderno"
                                    disabled={viewMode}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Invoice No."
                                    value={renewalState.Invoiceno}
                                    onChange={handleOnChange}
                                    id="invoiceNo"
                                    name="Invoiceno"
                                    disabled={viewMode}
                                />
                            </Grid>

                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Total Contract Value"
                                    placeholder={""}
                                    id="totalContractValue"
                                    name="totalContractValue"
                                    disabled
                                    value={renewalState.totalContractValue}
                                    isRefilByOtherField={true}

                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <FormControl fullWidth error={true}>
                                    <Box>
                                        <Typography variant="labelSm">Work Type</Typography>
                                    </Box>
                                    <FormGroup row sx={{ justifyContent: "space-between" }}>
                                        {workTypeOptions?.map((wt) => (
                                            <FormControlLabel
                                                key={wt.value}
                                                control={
                                                    <Checkbox
                                                        checked={renewalState.workType.includes(wt.value)}
                                                        onChange={handleWorkTypeChange}
                                                        value={wt.value}
                                                        disabled={location.state.renewalmode || viewMode}
                                                    />
                                                }
                                                label={wt.label}
                                            />
                                        ))}
                                    </FormGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextFieldWithSearch
                                    label="Vendor Name"
                                    id="vendorName"
                                    name="vendorName"
                                    value={renewalState.vendorName}
                                    options={vendorNameOptions?.map(option => ({
                                        label: option?.label,
                                        value: option?.value,
                                        id: option?.id
                                    }))}
                                    onChange={handleOnChange}
                                    isMandatoryField={true}
                                    disabled={location.state.renewalmode || viewMode}
                                    error={!!errors.vendorName}
                                    errorMessage={errors.vendorName}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Vendor Code"
                                    placeholder={""}
                                    id="vendorCode"
                                    name="vendorCode"
                                    disabled
                                    value={renewalState.vendorCode}
                                    isRefilByOtherField={true}
                                />
                            </Grid>


                            <Grid item xs={6} md={3.8}>
                                <CustomSelectInput
                                    label="Vendor Status"
                                    value={renewalState.vendorStatus}
                                    onChange={handleOnChange}
                                    options={vendorStatusOptions}
                                    id="vendorStatus"
                                    name="vendorStatus"
                                    disabled={location.state.renewalmode || viewMode}
                                />
                            </Grid>

                            {(getNewContractId && !checkIsRenewalForm) && (

                                <>
                                    <Grid item xs={6} md={3.8}>
                                        <CustomSelectInput
                                            disableClearable={true}
                                            label="Status (Note: To Terminate, select 'Inactive')"
                                            value={renewalState?.renewalModeStatus || ""}
                                            onChange={handleOnChange}
                                            options={statusOptions}
                                            id="renewalModeStatus"
                                            name="renewalModeStatus"
                                            disabled={disableStatusField?.renewalMode || viewMode}
                                        />
                                    </Grid>
                                    {reasonFieldForRenewalEditMode && (<Grid item xs={6} md={3.8}>
                                        <CustomTextArea
                                            label="Reason"
                                            id="renewalModeReason"
                                            name="renewalModeReason"
                                            value={renewalState?.renewalModeReason || ""}
                                            onChange={(e) => handleOnChange(e, "renewalModeReason")}
                                            maxlength={250}
                                            error={!!errors?.renewalModeReason}
                                            helperText={errors?.renewalModeReason || ""}
                                            isMandatoryField={reasonFieldForRenewalEditMode}
                                            disabled={disableStatusField?.renewalMode || viewMode}
                                        />
                                    </Grid>)}
                                </>
                            )}
                        </Grid>
                        <Grid item xs={6} md={3.8}>
                            <FormLabel component="legend">Rating</FormLabel>
                            <Stack spacing={6}>
                                <Rating
                                    name="rating"
                                    value={renewalState.rating}
                                    onChange={handleRatingChange}
                                    size="large"
                                    sx={{
                                        fontSize: '3rem',
                                        '& .MuiRating-icon': {
                                            marginRight: '2rem',
                                        },
                                    }}
                                    disabled={location.state.renewalmode || viewMode}
                                />
                            </Stack>
                        </Grid>
                    </Box>
                </form>
            </DefaultCard>
            <Box mb={2} />
            <DefaultCard sx={{ height: "100%" }}>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant='title2'>Renewal Process Status</Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    {!viewMode && (<AddRowButton onClick={handleOpenDialog} />)}
                </Stack>
                {/* <Box mb={2} /> */}
                {/* <form onSubmit={handleSubmit} style={{ position: "relative" }}>
                    <Box mb={2}>
                        <Grid container gap={3}>
                            <Grid item xs={6} md={3.8}>
                                <CustomSelectInput
                                    label="Flag"
                                    value={renewalState.flag}
                                    onChange={handleOnChange}
                                    options={flagOptions}
                                    id="flag"
                                    name="flag"
                                    disabled={location.state.renewalmode || viewMode}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomSelectInput
                                    label="PIC"
                                    value={renewalState.pic}
                                    onChange={handleOnChange}
                                    options={picOptions}
                                    id="pic"
                                    name="pic"
                                    disabled={location.state.renewalmode || viewMode}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomSelectInput
                                    label="Activity"
                                    value={renewalState.activity}
                                    onChange={handleOnChange}
                                    options={activityOptions}
                                    id="activity"
                                    name="activity"
                                    disabled={location.state.renewalmode || viewMode}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomDatePicker
                                    label="Date/Time (Auto)"
                                    id="date/time"
                                    name="date/time"
                                    value={formatDateForDisplay(renewalState.dateTime)}
                                    onDateChange={handleDateTimeAuto}
                                    disabled={location.state.renewalmode || viewMode}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextArea
                                    label="Remarks"
                                    id="remarks"
                                    name="remarks"
                                    value={renewalState.remarks}
                                    onChange={(e) => handleOnChange(e, "remarks")}
                                    maxlength={180}
                                    disabled={location.state.renewalmode || viewMode}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </form> */}
                <CustomAgGrid
                    refSet={gridAPI}
                    rows={rows}
                    columns={columns}
                    setGridApi={setGridApi}
                    handleEdit={!viewMode ? (row) => handleEdit(row) : null}
                />

            </DefaultCard>
            {(getNewContractId) && (
                <DefaultCard sx={{ height: "100%", mt: 2 }}>
                    <Box mb={2}>
                        <Typography variant="title2">History</Typography>
                    </Box>
                    <ContractHistory service={location?.state?.service} headerId={location?.state?.id} />
                </DefaultCard>
            )}

            <RenewalProcessStatusModal
                open={dialogOpen}
                onClose={handleCloseDialog}
                onConfirm={handleConfirm}
                editMode={editData}
                rows={rows}
                setRows={setRows}
            />
        </>
    )

})

export default ContractEditForm;